<template>
  <section class="mt-10">
    <div class="mb-3 flex items-center justify-between">
      <h2
        class="text-[15.5px] font-extrabold tracking-[0.015em] text-[#2e234b] md:text-[18.56px] md:tracking-[0.02em]"
      >
        {{ title }}
      </h2>
      <NuxtLink
        v-if="seeAllTo"
        :to="seeAllTo"
        class="text-[14px] font-extrabold text-[#2e234b] md:text-[18.56px]"
      >
        {{ $t("cardRail.seeAll") }}
      </NuxtLink>
      <button
        v-else-if="showSeeAll"
        class="bg-transparent text-[14px] font-extrabold text-[#2e234b] md:text-[18.56px]"
        @click="seeAll"
      >
        {{ $t("cardRail.seeAll") }}
      </button>
    </div>

    <div
      v-if="loading && items.length === 0"
      class="space-y-4"
      aria-label="Loading cards"
    >
      <div class="-mx-4 overflow-x-auto px-4 pb-8 md:hidden">
        <div class="flex gap-4 pb-2">
          <div
            v-for="n in mobileSkeletonCount"
            :key="`mobile-skeleton-${n}`"
            class="w-full max-w-sm shrink-0 animate-pulse"
          >
            <div class="aspect-square rounded-3xl bg-gray-200"></div>
            <div class="space-y-2 px-1 pt-4">
              <div class="h-4 w-5/6 rounded bg-gray-200"></div>
              <div class="h-3 w-1/2 rounded bg-gray-200"></div>
              <div class="mt-3 h-3 w-2/3 rounded bg-gray-200"></div>
            </div>
          </div>
        </div>
      </div>

      <div
        class="hidden gap-6 md:grid md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5"
      >
        <div
          v-for="n in desktopSkeletonCount"
          :key="`desktop-skeleton-${n}`"
          class="w-full max-w-sm animate-pulse"
        >
          <div class="aspect-square rounded-3xl bg-gray-200"></div>
          <div class="space-y-2 px-1 pt-4">
            <div class="h-4 w-5/6 rounded bg-gray-200"></div>
            <div class="h-3 w-1/2 rounded bg-gray-200"></div>
            <div class="mt-3 h-3 w-2/3 rounded bg-gray-200"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Mobile: horizontal scroll -->
    <div v-else class="-mx-4 overflow-x-auto px-4 pb-8 md:hidden">
      <div class="flex gap-4 pb-2">
        <template v-for="it in items" :key="it.id">
          <slot name="item" :item="it">
            <component
              :is="itemComponent || EventCard"
              class="shrink-0"
              v-bind="it"
            />
          </slot>
        </template>
      </div>
    </div>

    <!-- Desktop grid -->
    <div
      v-if="!loading || items.length > 0"
      class="hidden gap-6 md:grid md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5"
    >
      <template v-for="it in items" :key="it.id">
        <slot name="item" :item="it">
          <component :is="itemComponent || EventCard" v-bind="it" />
        </slot>
      </template>
    </div>

    <div
      v-if="items.length === 0 && !loading"
      class="mt-6 flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-zinc-200 p-8 text-center text-zinc-600"
    >
      <slot name="empty">
        <h3 class="text-lg font-semibold text-indigo-950">
          {{ $t("cardRail.noItemsFound") }}
        </h3>
        <p class="text-sm">{{ $t("cardRail.tryWideningSearch") }}</p>
      </slot>
    </div>

    <!-- Keep pagination operable for keyboard and assistive-technology users. -->
    <div v-if="hasMore && items.length > 0" class="mt-4 flex justify-center">
      <button
        class="btn-secondary px-4 py-2"
        :disabled="loading"
        @click="seeMore"
      >
        {{ loading ? $t("cardRail.loadingMore") : $t("cardRail.loadMore") }}
      </button>
    </div>
  </section>
</template>

<script setup lang="ts">
import type { Component } from "vue";
import EventCard from "~/components/cards/EventCard.vue";
import type { UiCard } from "~~/shared/models/cards";

const mobileSkeletonCount = 3;
const desktopSkeletonCount = 4;

const props = withDefaults(
  defineProps<{
    title: string;
    items: UiCard[];
    railKey?: string;
    hasMore?: boolean;
    loading?: boolean;
    itemComponent?: Component;
    seeAllTo?: string;
    showSeeAll?: boolean;
  }>(),
  {
    railKey: "",
    hasMore: false,
    loading: false,
    itemComponent: undefined,
    seeAllTo: undefined,
    showSeeAll: true,
  },
);
const emit = defineEmits<{
  (e: "seeAll", key: string): void;
  (e: "loadMore"): void;
}>();

function seeAll() {
  emit("seeAll", props.railKey ?? "");
}

function seeMore() {
  if (props.loading) return;
  emit("loadMore");
}
</script>
